/**
 * DAA encounter lines (ENG-345): for every active `daa_alerts` pair, a line
 * between the ownship and the intruder where each is on the map right now, a
 * ring round the intruder, and a range / vertical-separation label at the
 * midpoint, all coloured by ASTM F3442 level.
 *
 * The alert itself carries no positions, so both ends come from what the map
 * already tracks: the ownship by resolving its flight declaration id to a drone
 * serial, the intruder either a drone (`uas_serial`) or a crewed aircraft (ICAO
 * hex, from the fused feed). An end the map cannot place falls back to the
 * position in Blender's incident log; simulated drones exist only there. A pair
 * with an end neither can place is skipped.
 */
import type { OiSummary } from '@/lib/api/types';
import { DAA_POSITION_FRESH_MS, useDaaPositionsStore } from '@/lib/daa/incidentPositions';
import { aircraftForOi } from '@/lib/oi/aircraftForOi';
import { queryClient } from '@/lib/query';
import { useDaaAlertsStore } from '@/lib/store/daaAlerts';
import type { DaaEncounter } from '@/lib/store/daaAlerts';
import { type DroneLive, useDronesStore } from '@/lib/store/drones';
import { useTargetsStore } from '@/lib/store/targets';
import { useUiStore } from '@/lib/store/ui';
import type { GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';

const SRC = 'src-daa-lines';
const LINE_LAYER = 'lyr-daa-lines';
const RING_LAYER = 'lyr-daa-rings';
const ENDS_LAYER = 'lyr-daa-ends';
const LABEL_LAYER = 'lyr-daa-labels';

/** Level 0..3 colours, as in Brayden's viewer. */
export const DAA_LEVEL_COLOR = ['#8b98a8', '#39c5cf', '#e3a526', '#f85149'] as const;

type LngLat = [number, number];

const droneAt = (
  byId: Readonly<Record<string, DroneLive>>,
  id: string | null | undefined,
): LngLat | null => {
  if (!id) return null;
  const d = byId[id] ?? Object.values(byId).find((x) => x.id.toLowerCase() === id.toLowerCase());
  return d ? [d.telemetry.lng, d.telemetry.lat] : null;
};

/** The alert names the ownship only by its flight declaration id, which is the
 *  OI id: resolve it through the REST list, Blender's own OIs, then the live
 *  per-drone OI envelopes. */
function ownshipSerial(flightId: string, byId: Readonly<Record<string, DroneLive>>): string | null {
  const blender = queryClient.getQueryData<OiSummary[]>(['oi', 'blender']) ?? [];
  return (
    aircraftForOi(flightId, byId) ?? blender.find((o) => o.oi_id === flightId)?.aircraft ?? null
  );
}

function intruderPosition(
  e: DaaEncounter,
  byId: Readonly<Record<string, DroneLive>>,
): { at: LngLat; name: string } | null {
  const hex = (
    e.intruderIcao ?? (e.intruderIdType === 'icao' ? e.intruderId : null)
  )?.toLowerCase();
  if (hex) {
    for (const t of Object.values(useTargetsStore.getState().byId)) {
      if (t.icao_hex?.toLowerCase() === hex) return { at: [t.lng, t.lat], name: t.callsign };
    }
    return null;
  }
  const serial = e.intruderAircraftId ?? e.intruderId;
  const at = droneAt(byId, serial);
  return at ? { at, name: byId[serial]?.telemetry.callsign || serial.slice(-6) } : null;
}

export function daaLineFeatures(): GeoJSON.Feature[] {
  const ui = useUiStore.getState();
  if (ui.activeContext !== 'drones' || !ui.showDaaLines) return [];
  const encounters = Object.values(useDaaAlertsStore.getState().byKey);
  if (encounters.length === 0) return [];
  const byId = useDronesStore.getState().byId as Record<string, DroneLive>;
  const logged = useDaaPositionsStore.getState().byKey;
  const now = Date.now();
  const features: GeoJSON.Feature[] = [];
  for (const e of encounters) {
    const resolved = e.status === 'resolved';
    const level = resolved ? 0 : e.level;
    // Level 0 is DAA tracking a pair, not alerting on it.
    if (level === 0 && !resolved) continue;
    const row = logged[e.key];
    const fresh = row && now - row.receivedAt <= DAA_POSITION_FRESH_MS ? row : null;
    const liveIntruder = intruderPosition(e, byId);
    const liveOwn = droneAt(byId, ownshipSerial(e.ownshipFlightId, byId));
    const intruder =
      liveIntruder ?? (fresh ? { at: fresh.intruder, name: e.intruderId.slice(-6) } : null);
    const own = liveOwn ?? fresh?.own ?? null;
    if (!intruder || !own) continue;
    const color = DAA_LEVEL_COLOR[level];
    const props = { color, level, resolved, key: e.key };
    const vsep = e.verticalSepM == null ? '' : ` ↕${Math.round(e.verticalSepM)}m`;
    const range = e.rangeM == null ? '' : ` ${Math.round(e.rangeM)} m`;
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [own, intruder.at] },
      properties: { ...props, kind: 'line', width: level >= 3 ? 3 : 2 },
    });
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: intruder.at },
      properties: { ...props, kind: 'ring' },
    });
    // A simulated drone has no marker of its own: mark the ends that came from
    // the incident log. A live drone or aircraft is already on the map.
    if (!liveOwn) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: own },
        properties: { ...props, kind: 'own' },
      });
    }
    if (!liveIntruder) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: intruder.at },
        properties: { ...props, kind: 'intruder' },
      });
    }
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [(own[0] + intruder.at[0]) / 2, (own[1] + intruder.at[1]) / 2],
      },
      properties: { ...props, kind: 'label', label: `${intruder.name}${range}${vsep}` },
    });
  }
  return features;
}

let lastWasEmpty = false;

export function syncDaaLines(map: MapboxMap) {
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  if (!src) return;
  const features = daaLineFeatures();
  if (features.length === 0 && lastWasEmpty) return;
  lastWasEmpty = features.length === 0;
  src.setData({ type: 'FeatureCollection', features });
}

export function subscribeDaaLines(map: MapboxMap): () => void {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer(LINE_LAYER)) {
    map.addLayer({
      id: LINE_LAYER,
      type: 'line',
      source: SRC,
      filter: ['==', ['get', 'kind'], 'line'],
      layout: { 'line-cap': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': 0.95,
        // Resolved pairs and level 0 are drawn dashed grey.
        'line-dasharray': ['case', ['get', 'resolved'], ['literal', [2, 2]], ['literal', [1, 0]]],
      },
    });
  }
  if (!map.getLayer(RING_LAYER)) {
    map.addLayer({
      id: RING_LAYER,
      type: 'circle',
      source: SRC,
      filter: ['==', ['get', 'kind'], 'ring'],
      paint: {
        'circle-radius': ['case', ['>=', ['get', 'level'], 3], 16, 12],
        'circle-color': 'rgba(0,0,0,0)',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer(ENDS_LAYER)) {
    map.addLayer({
      id: ENDS_LAYER,
      type: 'circle',
      source: SRC,
      filter: ['in', ['get', 'kind'], ['literal', ['own', 'intruder']]],
      paint: {
        'circle-radius': 6,
        // Ownship white, intruder in its level colour.
        'circle-color': ['case', ['==', ['get', 'kind'], 'own'], '#ffffff', ['get', 'color']],
        'circle-stroke-color': [
          'case',
          ['==', ['get', 'kind'], 'own'],
          ['get', 'color'],
          '#ffffff',
        ],
        'circle-stroke-width': 2.5,
      },
    });
  }
  if (!map.getLayer(LABEL_LAYER)) {
    map.addLayer({
      id: LABEL_LAYER,
      type: 'symbol',
      source: SRC,
      filter: ['==', ['get', 'kind'], 'label'],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-offset': [0, -1],
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': '#0b0f14',
        'text-halo-width': 2,
      },
    });
  }
  lastWasEmpty = false;
  syncDaaLines(map);
  let rafId: number | null = null;
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      syncDaaLines(map);
    });
  };
  const unsubDaa = useDaaAlertsStore.subscribe((s) => s.byKey, schedule);
  const unsubDrones = useDronesStore.subscribe((s) => s.byId, schedule);
  const unsubTargets = useTargetsStore.subscribe((s) => s.byId, schedule);
  const unsubLogged = useDaaPositionsStore.subscribe((s) => s.byKey, schedule);
  const unsubUi = useUiStore.subscribe((s) => `${s.activeContext}|${s.showDaaLines}`, schedule);
  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    unsubDaa();
    unsubDrones();
    unsubTargets();
    unsubLogged();
    unsubUi();
    for (const id of [LABEL_LAYER, ENDS_LAYER, RING_LAYER, LINE_LAYER]) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
    if (map.getSource(SRC)) map.removeSource(SRC);
  };
}
